/**
 * Game registry: maps each `GameId` to its `GameModule`. The platform
 * layer (router, host/player views, lobby service) consumes games through
 * this registry only — it never imports a game's module directly.
 *
 * The map is `Partial` so additional games can be declared in
 * `platform/types.ts` (`GameId`) before their module is implemented; calls
 * to `getGameModule` for those ids will return `undefined` and the caller
 * is expected to surface a "coming soon" / 404 state.
 */

import type { GameId } from '@mdev/common'
import type { GameModule } from './module'
import { camelUpModule } from './camel-up/module'
import { scotlandYardModule } from './scotland-yard/module'

/**
 * Registered game modules. Keys are `GameId` values declared in
 * `platform/types.ts`. Add new games here as they're implemented.
 *
 * Note: the value type is `GameModule<any, any>` because each game has its
 * own `TState` / `TSettings` shape, and a single map can't preserve those
 * generics across keys. Callers narrow back to the concrete module's type
 * by importing it directly when they need precise types.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const GAMES: Partial<Record<GameId, GameModule<any, any>>> = {
  'scotland-yard': scotlandYardModule,
  'camel-up': camelUpModule,
}

/** Convenience lookup by id. Returns `undefined` for unregistered games. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getGameModule( id: GameId ): GameModule<any, any> | undefined {
  return GAMES[ id ]
}
